import "./App.css";

import mapboxgl from "mapbox-gl";
import mixpanel from "mixpanel-browser";
import React, { Suspense, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Navigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import Loader from "./components/loader/loader.component";
import ToastNotification from "./components/toastNotification/ToastNOtification";
import CacheBuster from "./helper/cachebuster";
import useCognitoTokenExchange from "./hooks/useCognitoTokenExchange";
import useSilentCognitoAuth from "./hooks/useSilentCognitoAuth";
import { selectToken } from "./redux/token/token.selector";
import { setCurrentUser } from "./redux/user/user.action";
import { selectCurrentUser } from "./redux/user/user.selector";
import Router from "./route/router";
import { getUser } from "./services/authentication.service";

mixpanel.init(import.meta.env.VITE_REACT_APP_MIXPANEL_TOKEN);

mapboxgl.accessToken = import.meta.env.VITE_REACT_APP_MAPBOX_API_KEY
	? import.meta.env.VITE_REACT_APP_MAPBOX_API_KEY
	: "";

function App(props) {
	const loading = () => <Loader />;

	const getCurrentUser = useCallback(() => props.user, [props.user]);
	const getToken = useCallback(() => props.token, [props.token]);

	return (
		<BrowserRouter>
			<Suspense fallback={loading()}>
				<Router props={{ user: getCurrentUser(), token: getToken() }} />
				<ToastNotification />
			</Suspense>
		</BrowserRouter>
	);
}

const mapStateToProps = createStructuredSelector({
	token: selectToken,
	user: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
	setcurrentuser: (user) => dispatch(setCurrentUser(user)),
});

const AppWithCacheBuster = connect(
	mapStateToProps,
	mapDispatchToProps,
)((props) => {
	const { setcurrentuser } = props;

	// If the user arrived with a cognito_token in the URL (from core.cresa.com),
	// exchange it for a Bluechip session before anything else runs
	const { isExchanging } = useCognitoTokenExchange();

	// Silently check for an existing Cognito session when
	// the user has no local Bluechip session
	// const { isCheckingCognito } = { isCheckingCognito: false };
	const { isCheckingCognito } = useSilentCognitoAuth(props.token, props.user);

	useEffect(() => {
		const getData = async () => {
			// Skip the getUser call if a cognito_token exchange is in progress.
			if (isExchanging) {
				return;
			}

			if (!!props.token && !props.user) {
				const res = await getUser();
				if (res?.data?.response_type === "success") {
					setcurrentuser(res.data.data);
				} else {
					return <Navigate to={"/login"} replace />;
				}
			}
		};
		getData();
	}, [props.token, props.user, setcurrentuser, isExchanging]);

	useEffect(() => {
		document.title = import.meta.env.VITE_APP_NAME;
	}, []);

	return (
		<CacheBuster>
			{({ cacheloading, isLatestVersion, refreshCacheAndReload }) => {
				useEffect(() => {
					if (!cacheloading && !isLatestVersion) {
						refreshCacheAndReload();
					}
				}, [cacheloading, isLatestVersion, refreshCacheAndReload]);

				if (cacheloading) return null;

				if (isExchanging) {
					return <Loader />;
				}

				if (isCheckingCognito) {
					return <Loader />;
				}

				return <App {...props} />;
			}}
		</CacheBuster>
	);
});

export default AppWithCacheBuster;
